import React, { useState } from "react";
import { Sparkles, Play, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { runAIPrompt } from "@/lib/ai";
import ConfirmRegenerateDialog from "./ConfirmRegenerateDialog";

/** Reusable "AI έγγραφα αυτής της κατηγορίας" block, ενσωματωμένο μέσα σε ένα tab
 * (π.χ. CaseOverview, Timeline, Witnesses...). Δείχνει τα ήδη παραγμένα έγγραφα
 * της κατηγορίας + κουμπί δημιουργίας· αν υπάρχει ήδη έγγραφο για την ίδια
 * κατηγορία (+witnessId, όταν δίνεται), ζητά επιβεβαίωση πριν δημιουργήσει άλλο. */
export default function AICategoryPanel({
  caseId, promptKey, titleEl, outputs, onGenerated, witnessId = null, model = "claude",
}) {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const matching = outputs.filter(
    o => o.prompt_key === promptKey && (!witnessId || o.witness_id === witnessId)
  );
  const latest = matching[0];

  const doGenerate = async () => {
    setRunning(true);
    try {
      const payload = { prompt_key: promptKey, case_id: caseId, model };
      if (witnessId) payload.witness_id = witnessId;
      const result = await runAIPrompt(payload);
      onGenerated({
        id: result.output_id, case_id: caseId, prompt_key: promptKey,
        title: result.title, model: result.model, confidence: result.confidence,
        output: result.output, created_at: result.completed_at,
        witness_id: witnessId || null,
      });
      toast.success(`AI ✓ ${result.title}`);
    } catch (e) {
      toast.error(e.message || "Σφάλμα AI");
    } finally {
      setRunning(false);
    }
  };

  const handleClick = () => {
    if (matching.length > 0) setConfirmOpen(true);
    else doGenerate();
  };

  return (
    <div className="surface-card p-5" data-testid={`ai-panel-${promptKey}`}>
      <div className="flex items-center justify-between mb-3 gap-3">
        <h3 className="font-heading font-semibold text-[14px] flex items-center gap-2">
          <Sparkles className="size-4 gold-text"/>{titleEl}
        </h3>
        <button
          data-testid={`ai-panel-run-${promptKey}`}
          onClick={handleClick} disabled={running}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[hsl(var(--gold)/0.12)] gold-text gold-border border text-[12.5px] font-medium hover:bg-[hsl(var(--gold)/0.2)] transition-colors disabled:opacity-60"
        >
          {running
            ? <><Sparkles className="size-3.5 animate-pulse-soft"/>AI thinking…</>
            : <><Play className="size-3.5"/>Δημιουργία με AI</>}
        </button>
      </div>

      {matching.length > 0 ? (
        <div className="space-y-1.5">
          {matching.slice(0, 3).map(o => (
            <a key={o.id} href="/reports"
               className="flex items-center justify-between gap-2 text-[12.5px] surface-card-hover rounded-md px-2.5 py-2">
              <span className="truncate">{o.title}</span>
              <span className="flex items-center gap-2 text-muted-foreground font-mono text-[11px] shrink-0">
                {new Date(o.created_at).toLocaleDateString("el-GR")}
                <ChevronRight className="size-3.5"/>
              </span>
            </a>
          ))}
        </div>
      ) : (
        <p className="text-[12px] text-muted-foreground font-mono">
          Δεν έχει δημιουργηθεί ακόμα έγγραφο AI αυτής της κατηγορίας.
        </p>
      )}

      <ConfirmRegenerateDialog
        open={confirmOpen} onOpenChange={setConfirmOpen}
        existingDate={latest?.created_at}
        onConfirm={() => { setConfirmOpen(false); doGenerate(); }}
      />
    </div>
  );
}
